import React, {
  useCallback, useEffect, useState, useMemo, ReactElement, Fragment,
} from 'react';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import ListItemImage from '../../../components/list-item-image/list-item-image.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import SearchResultTable from '../search-result-table/search-result-table.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  SEARCH_RESULT_GROUP_ACTIVITIES, SEARCH_RESULT_GROUP_ASSETS, SEARCH_RESULT_GROUP_COMPANIES, SEARCH_RESULT_GROUP_DEALS,
  SEARCH_RESULT_GROUP_DEALS_CLOSED, SEARCH_RESULT_GROUP_PEOPLE,
} from '../../../constants/system/search-result-groups';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const SearchResultGroup: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    searchResultData,
    searchResultTotal,
    headerTitle,
    groupName,
    tableSearchResultColumnList,
    tableSearchResultSubGroupColumnList,
    primaryColumnName,
    secondaryColumnName,
    defaultSortState,
    subItemsExpandedMap,
    onRowClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const searchResultTotalLabel: string = searchResultTotal ? ` (${searchResultTotal})` : '';
  const commonHeaderTitle: string = headerTitle ? `${headerTitle}${searchResultTotalLabel}` : '';

  const [sortParams, setSortParams]: [SortParams, Function] = useState(defaultSortState);

  const TYPE_TO_DEFAULT_PHOTO_MAP: Record<string, ReactElement> = useMemo((): Record<string, ReactElement> => ({
    [SEARCH_RESULT_GROUP_COMPANIES]: CompanyDefaultImage,
    [SEARCH_RESULT_GROUP_PEOPLE]: PersonDefaultImage,
    [SEARCH_RESULT_GROUP_DEALS]: AssetDefaultImage,
    [SEARCH_RESULT_GROUP_DEALS_CLOSED]: AssetDefaultImage,
    [SEARCH_RESULT_GROUP_ACTIVITIES]: AssetDefaultImage,
    [SEARCH_RESULT_GROUP_ASSETS]: AssetDefaultImage,
  }), []);

  const searchResultRowClick: Function = useCallback((entityId: number, dealId: number): void => {
    if (onRowClick) {
      onRowClick(entityId, dealId);
    }
  }, []);

  useEffect((): void => {
    if (!sortParams && defaultSortState) {
      setSortParams(defaultSortState);
    }
  }, [defaultSortState]);

  return (
    <Fragment>
      {
        searchResultData && !!searchResultData.length && (
          <ExpansionGroup
            isTableGroup
            isScrollable
            isDefaultExpanded
            withExpandedOffset
            headerComponent={commonHeaderTitle}
          >
            {
              !isSmallScreen ? (
                <SearchResultTable
                  searchResultData={searchResultData}
                  tableSearchResultColumnList={tableSearchResultColumnList}
                  tableSearchResultSubGroupColumnList={tableSearchResultSubGroupColumnList}
                  defaultSortState={defaultSortState}
                  subItemsExpandedMap={subItemsExpandedMap}
                  onRowClick={onRowClick}
                />
              ) : (
                <List>
                  {
                    searchResultData.map((searchResultItem: SearchResultItem): ReactElement => (
                      <ListItem
                        isButton
                        isExtended
                        key={searchResultItem.id}
                        onClick={(): void => {
                          searchResultRowClick(searchResultItem.id, searchResultItem.dealId || null);
                        }}
                      >
                        {
                          (searchResultItem.photo || TYPE_TO_DEFAULT_PHOTO_MAP[groupName]) && (
                            <ListItemImage
                              text={searchResultItem.name}
                              link={searchResultItem.photo || TYPE_TO_DEFAULT_PHOTO_MAP[groupName]}
                            />
                          )
                        }
                        <ListItemText isBlock>
                          {searchResultItem[primaryColumnName]}
                        </ListItemText>
                        <ListItemText
                          isBlock
                          isBlockRight
                          subText={searchResultItem[secondaryColumnName]}
                        />
                      </ListItem>
                    ))
                  }
                </List>
              )
            }
          </ExpansionGroup>
        )
      }
    </Fragment>
  );
});

export default SearchResultGroup;
